<template>
<ItpBox
  direction="column"
  class="p-3"
  v-if="loaded"
>
  <ItpBox
    alignItems="center"
    class="mb-3"
  >
    <strong>
      {{ 'Invoicing configuration'|pgettext('Settings') }}
    </strong>
    <ItpButton
      icon="fa-sync-alt"
      :text="'Reload'|gettext"
      variant="light"
      size="sm"
      :spinning="actions.reload.isRunning"
      class="ml-3"
      @click.prevent.stop="evh_3343864469950044_clickPreventStop($event, {})"
    >
    </ItpButton>
  </ItpBox>
  <ItpDataTable
    tableId="Settings.Invoicing"
    editable
    :isRowEditable="p => !p.locked"
    :value="items"
    no-create-row
    no-delete-row
    :globs="{ configureNumbering, configureProvider, setAsDefaultNumbering }"
    :columns="b_5962862420439144_columns"
    class="h-100"
    ref="table"
    v-if="loaded"
    @save="evh_5962862420439144_save($event, {})"
  >
  </ItpDataTable>
</ItpBox>
</template>

<script lang="ts">

import * as _ from 'lodash';
import Big from 'big.js';
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import {
  addRoute, UserComponentMixin, ComponentValueCache, actions
} from '@integro/core';

addRoute(
  {
    name: "settings.invoicing",
    path: "invoicing",
    component: "SettingsInvoicing"
  }
)

@Component()
export default class SettingsInvoicing extends mixins(UserComponentMixin) {
  componentMetadata = {
    kind: "page",
  };

  beforeCreate() {
  }

  updateItem(...args: any[]) {
    const [data, extras] = args;

    return this.$fn.crud('invoicing_settings').update(
      _.extend(
        _.pick(data, [
          'company',
          'draft_numbering',
          'default_numbering',
          'invoicing_mode',
          'invoicing_provider',
          'template'
        ]),
        extras
      ),
      {
        parameters: { company_id: data.company.id }
      }
    )
  }

  onSave(...args: any[]) {
    const [$event] = args;
    const row = $event.data.row;

    return $event.data.waitFor(this.updateItem(row));
  }

  configureNumbering(...args: any[]) {
    const [value, row] = args;
    const updateItem = this.updateItem;

    return this.$imodal
      .show({
        name: 'Numbering',
        props: {
          company: row.company,
          oid: value ? value.id : null,
          objectType: 'invoice:out',
        }
      })
      .then(result => {
        if (result) {
          this.loadData();
        }
      })
  }

  setAsDefaultNumbering(...args: any[]) {
    const [default_numbering, row] = args;

    this
      .updateItem(row, { default_numbering })
      .then(result => {
        if (result) {
          this.loadData();
        }
      })
  }

  configureProvider(...args: any[]) {
    const [value, row] = args;
    const updateItem = this.updateItem;

    return this.$imodal
      .show({
        name: 'InvoicingProviderSettings',
        props: {
          item: _.cloneDeep(value),
          saving(invoicing_provider) {
            return updateItem(row, { invoicing_provider });
          }
        }
      })
      .then(result => {
        if (result) {
          row.setData(result)
        }
      })
  }

  @Prop({
    type: String,
    default: "md",
  })
  size!: string;

  @Watch('size')
  onSize(newValue: any, oldValue: any) {
    if (_.isEqual(newValue, oldValue)) return;
    this.loadDataThrottled("size")
  }

  items!: any;

  async $$load_items() {
    return this.$fn.fetch('list_invoicing_settings').then(data =>
      _.orderBy(data, ['company.name']).map(p => _.extend(p, { id: p.company.id }))
    )
  }

  numberings!: any;

  async $$load_numberings() {
    return this.$fn.fetch('list_numbering').then(data => _.filter(data, { resource_type: 'invoice:out' }))
  }

  templates!: any;

  async $$load_templates() {
    return this.$fn.fetch('list_template').then(data => _.filter(data, { template_type: 'html', object_type: 'invoice' }))
  }

  invoicingModes!: any;

  async $$load_invoicingModes() {
    return this.$fn.getEnumValuesFromSchema('InvoicingMode')
  }

  ux!: any;
  b_5962862420439144_columns!: any;

  async $$load_b_5962862420439144_columns() {
    return [{
      name: "company",
      field: "company.name",
      header: this.$fn.pgettext("Settings|TableHeader", "Company"),
      width: "220px",
      editable: false,
    }
      ,
    {
      name: "invoicing_mode",
      header: this.$fn.pgettext("Settings|TableHeader", "Invoicing mode"),
      width: "160px",
      type: "select2",
      options: this.invoicingModes,
      valueField: "value",
      textField: "text",
    }
      ,
    {
      name: "draft_numbering",
      header: this.$fn.pgettext("Settings|TableHeader", "Draft numbering"),
      width: "260px",
      type: "select2",
      options: this.numberings,
      textField: "description",
      path: "description",
      bindObject: true,
      clearable: true,
    }
      ,
    {
      name: "numbering",
      header: this.$fn.pgettext("Settings|TableHeader", "Numbering"),
      width: "260px",
      editable: false,
      formatter: {
        name: "template",
        value: "<div>\n\
  <div\n\
    v-if=\"value && value.length\"\n\
    class=\"d-flex flex-column\"\n\
  >\n\
    <div\n\
      v-for=\"item in value\"\n\
      :key=\"item.id\"\n\
    >\n\
      <ItpButton\n\
        v-if=\"item.is_default\"\n\
        variant=\"light\"\n\
        @click.prevent.stop=\"globs.configureNumbering(item, row)\"\n\
      >\n\
        <ItpIcon icon=\"icon-park-outline:dot\"></ItpIcon>\n\
        {{ item.description }}\n\
      </ItpButton>\n\
\n\
      <b-dropdown\n\
        v-if=\"!item.is_default\"\n\
        variant=\"light\"\n\
        split\n\
        split-class=\"border\"\n\
        toggle-class=\"border\"\n\
        @click.prevent.stop=\"globs.configureNumbering(item, row)\"\n\
      >\n\
        <template slot=\"button-content\">\n\
          {{ item.description }}\n\
        </template>\n\
\n\
        <b-dropdown-item\n\
          @click.prevent.stop=\"globs.setAsDefaultNumbering(item, row)\"\n\
        >\n\
        Set as default\n\
        </b-dropdown-item>\n\
      </b-dropdown>\n\
    </div>\n\
\n\
    <a class=\"text-nowrap mr-auto\"\n\
        href=\"\"\n\
       @click.prevent.stop=\"globs.configureNumbering(null, row)\"\n\
    >\n\
      new...\n\
    </a>\n\
  </div>\n\
</div>\n\
",
      }
      ,
    }
      ,
    {
      name: "invoicing_provider",
      header: this.$fn.pgettext("Settings|TableHeader", "Provider settings"),
      width: "160px",
      editable: false,
      formatter: {
        name: "template",
        value: "<a href=\"\" @click.prevent.stop=\"globs.configureProvider(value, row)\">\n\
  <span class=\"text-nowrap\" v-if=\"value\">\n\
    {{ value.name }}\n\
  </span>\n\
  <span class=\"text-nowrap\" v-if=\"!value\">\n\
    unset\n\
  </span>\n\
</a>\n\
",
      }
      ,
    }
      ,
    {
      name: "template",
      header: this.$fn.pgettext("Settings|TableHeader", "Template"),
      width: "160px",
      type: "select2",
      options: this.templates,
      path: "name",
      textField: "name",
      bindObject: true,
      clearable: true,
    }
      ,
    ]
  }

  dataMembers = ['items', 'numberings', 'templates', 'invoicingModes', 'ux', 'b_5962862420439144_columns'];

  data() {
    return {
      ...this.mixinData(),
      actions: this.getActions(),
      ...{
        items: null,
        numberings: null,
        templates: null,
        invoicingModes: null,
        ux: null,
        b_5962862420439144_columns: null,
      },
    }
  }

  get document() {
    if (this.loaded) {

      return
    }
    return null;
  }

  async evh_3343864469950044_clickPreventStop(event: Event, scope: Dictionary<any>) {
    return await this.actions.evh_3343864469950044_clickPreventStop.executeFromDOM(this, event, scope);
  }

  async act_5962862420439144_script_5658_getActionArgs_value($event: actions.ActionEvent) {
    // callableMethod act_5962862420439144_script_5658, alias=undefined
    this.onSave($event)
  }

  async act_5962862420439144_script_5658_getActionArgs($event: actions.ActionEvent): Promise<actions.ScriptActionArgs> {
    // parameterResolver name=act_5962862420439144_script_5658, alias=undefined
    return {
      value: () => this.act_5962862420439144_script_5658_getActionArgs_value($event),
    }
  }

  async evh_5962862420439144_save(event: Event, scope: Dictionary<any>) {
    return await this.actions.evh_5962862420439144_save.executeFromDOM(this, event, scope);
  }

  actions!: {
    act_3343864469950044_reloadComponentData_5656: actions.ReloadComponentDataAction;
    evh_3343864469950044_clickPreventStop_5655: actions.EventHandlerImpl;
    evh_3343864469950044_clickPreventStop: actions.EventHandlerGroup;
    act_5962862420439144_script_5658: actions.ScriptAction;
    evh_5962862420439144_save_5657: actions.EventHandlerImpl;
    evh_5962862420439144_save: actions.EventHandlerGroup;
  }

  getActions() {
    const act_3343864469950044_reloadComponentData_5656 = new actions.ReloadComponentDataAction(
      {
        displayName: "reload",
        events: [],
      }
    );
    const evh_3343864469950044_clickPreventStop_5655 = new actions.EventHandlerImpl(
      {
        action: act_3343864469950044_reloadComponentData_5656,
        event: "click.prevent.stop",
        displayName: "reload",
      }
    );
    const evh_3343864469950044_clickPreventStop = new actions.EventHandlerGroup(
      {
        handlers: [evh_3343864469950044_clickPreventStop_5655],
      }
    );
    const act_5962862420439144_script_5658 = new actions.ScriptAction(
      {
        actionArgs: this.act_5962862420439144_script_5658_getActionArgs,
        events: [],
      }
    );
    const evh_5962862420439144_save_5657 = new actions.EventHandlerImpl(
      {
        action: act_5962862420439144_script_5658,
        event: "save",
        displayName: "script",
      }
    );
    const evh_5962862420439144_save = new actions.EventHandlerGroup(
      {
        handlers: [evh_5962862420439144_save_5657],
      }
    );
    return {
      act_3343864469950044_reloadComponentData_5656,
      evh_3343864469950044_clickPreventStop_5655,
      evh_3343864469950044_clickPreventStop,
      act_5962862420439144_script_5658,
      evh_5962862420439144_save_5657,
      evh_5962862420439144_save,
      reload: act_3343864469950044_reloadComponentData_5656,
    }
  }
}

Vue.component("SettingsInvoicing", SettingsInvoicing);

</script>